<script lang="ts">
  /**
   * One row of a `Group`: a label on the left (after an optional badge or a
   * rank), and on the right a value, a control or a chevron. A hairline
   * separates it from the next row. TwinTen's settings and scores rows.
   *
   * - With `onclick` it is a button (a navigation row); without, plain text.
   * - `value` is set bold with tabular figures: a score, a count, a time. A
   *   row with a value is a table row: the label stays on one line and is cut
   *   short with an ellipsis rather than pushing the value down.
   * - `trailing` is a control at the right end (a `SegmentedControl`). The
   *   row wraps: a control that does not fit beside the label drops to a line
   *   of its own rather than squeezing the label, and there it starts at the
   *   row's left edge, under the badge (not pushed to the right).
   * - `rank` is a table's first column, 1.6rem wide, straight before the label.
   * - `tone`: `me` is the player's own row in a table (accent, heavier);
   *   `muted` is a row that only says something ("Nobody has finished yet",
   *   how a group says it is empty); `danger` a destructive row; `action` a
   *   row that does something here rather than going somewhere.
   * - `below` is a quieter second line under the label; it wraps in the
   *   width beside the control and never decides, by its length, whether the
   *   control fits there (only the label does). `under` a full-width line of
   *   the row's own (a control too wide to sit beside the label).
   *
   * Test ids: `testid` on the row, `valueTestid` on the value. No defaults.
   */
  import type { Snippet } from 'svelte';

  import Icon from './Icon.svelte';

  interface Props {
    readonly label: string;
    readonly below?: string | undefined;
    readonly value?: string | number | undefined;
    /** What leads the row: a `RowBadge`. */
    readonly badge?: Snippet | undefined;
    /** A table's first column: a rank. */
    readonly rank?: string | number | undefined;
    /** A glyph straight before the value (a trophy). */
    readonly mark?: Snippet | undefined;
    readonly trailing?: Snippet | undefined;
    /** A full-width line of the row's own, under the label. */
    readonly under?: Snippet | undefined;
    /** Draws a chevron: the row goes somewhere. */
    readonly chevron?: boolean;
    readonly tone?: 'default' | 'me' | 'muted' | 'danger' | 'action';
    readonly onclick?: (() => void) | undefined;
    readonly disabled?: boolean;
    /** The row opens a sheet or a dialog (`aria-haspopup="dialog"`). */
    readonly opensDialog?: boolean;
    /**
     * A pressable row that takes focus when its page opens (`data-autofocus`,
     * which `FullPage` and `Sheet` honour): the row a player came back from,
     * when a sub-page closes and the page it opened from is drawn again.
     */
    readonly autofocus?: boolean;
    readonly testid?: string | undefined;
    readonly valueTestid?: string | undefined;
  }
  const {
    label,
    below,
    value,
    badge,
    rank,
    mark,
    trailing,
    under,
    chevron = false,
    tone = 'default',
    onclick,
    disabled = false,
    opensDialog = false,
    autofocus = false,
    testid,
    valueTestid,
  }: Props = $props();

  const table = $derived(trailing === undefined && (value !== undefined || rank !== undefined));
</script>

{#snippet content()}
  {#if rank !== undefined}
    <span class="ui-row-rank">{rank}</span>
  {/if}
  <span class="ui-row-lead">
    {#if badge !== undefined}{@render badge()}{/if}
    <span class="ui-row-text">
      <span class="ui-row-label">{label}</span>
      {#if below !== undefined}
        <span class="ui-row-below">{below}</span>
      {/if}
    </span>
  </span>
  {#if value !== undefined || mark !== undefined}
    <span class="ui-row-value-group">
      {#if mark !== undefined}{@render mark()}{/if}
      {#if value !== undefined}
        <span class="ui-row-value" data-testid={valueTestid}>{value}</span>
      {/if}
    </span>
  {/if}
  {#if trailing !== undefined}
    <span class="ui-row-trailing">{@render trailing()}</span>
  {/if}
  {#if chevron}
    <span class="ui-row-chevron" aria-hidden="true"><Icon name="chevronRight" size={16} /></span>
  {/if}
  {#if under !== undefined}
    <span class="ui-row-under">{@render under()}</span>
  {/if}
{/snippet}

{#if onclick !== undefined}
  <button
    type="button"
    class="ui-row ui-row--{tone} ui-row--nav"
    class:ui-row--table={table}
    class:ui-row--trailing={trailing !== undefined}
    aria-haspopup={opensDialog ? 'dialog' : undefined}
    data-autofocus={autofocus ? '' : undefined}
    {disabled}
    data-testid={testid}
    {onclick}
  >
    {@render content()}
  </button>
{:else}
  <div
    class="ui-row ui-row--{tone}"
    class:ui-row--table={table}
    class:ui-row--trailing={trailing !== undefined}
    data-testid={testid}
  >
    {@render content()}
  </div>
{/if}

<style>
  .ui-row {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: var(--ui-space-2);
    border: 0;
    border-bottom: 1px solid var(--ui-line);
    background: transparent;
    padding: 0.85rem 1rem;
    text-align: left;
    color: var(--ui-ink);
    font: var(--ui-text-body);
  }

  .ui-row:last-child {
    border-bottom: 0;
  }

  .ui-row--table {
    flex-wrap: nowrap;
  }

  .ui-row--nav:disabled {
    opacity: 0.5;
  }

  /* Inside the group's rounded, clipped block: the ring is drawn inwards. */
  .ui-row--nav:focus-visible {
    outline: 3px solid var(--ui-accent);
    outline-offset: -3px;
  }

  .ui-row--muted {
    color: var(--ui-ink-2);
    font-size: 0.85rem;
  }

  .ui-row--me {
    font-weight: 800;
    color: var(--ui-accent);
  }

  .ui-row--action {
    color: var(--ui-accent);
    font-weight: 700;
  }

  .ui-row--danger {
    color: var(--ui-danger);
    font-weight: 700;
  }

  /* The label keeps its own width, so a control that does not fit beside it
     wraps below instead of squeezing it; in a table row it gives way. */
  .ui-row-lead {
    flex: 1 0 auto;
    max-width: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  .ui-row--table .ui-row-lead {
    flex: 1 1 auto;
  }

  /* Beside the label, the control is held off it by the lead's own margin
     (the lead grows, so the control ends at the right edge). Wrapped onto a
     line of its own, nothing pushes it: it starts at the left, as the row's
     content does. (A `margin-left: auto` would push it right on its own line.) */
  .ui-row--trailing .ui-row-lead {
    margin-right: 0.75rem;
  }

  .ui-row-rank {
    flex: 0 0 1.6rem;
    color: var(--ui-ink-2);
    font-variant-numeric: tabular-nums;
  }

  /* The text fills the lead, so the second line has the lead's width to wrap
     in. */
  .ui-row-text {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
  }

  .ui-row-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* The second line wraps in whatever width the label leaves and adds none of
     its own (width 0, at least the text's full width): only the label decides
     whether a control fits beside it, so a long `below` never pushes the
     control onto a line of its own (1.8.0). */
  .ui-row-below {
    width: 0;
    min-width: 100%;
    font: var(--ui-text-caption);
    font-weight: 400;
    line-height: 1.35;
    color: var(--ui-ink-2);
  }

  .ui-row-value-group {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: 100%;
    margin-left: 0.75rem;
  }

  .ui-row-value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    text-align: right;
  }

  .ui-row--me .ui-row-value {
    font-weight: 800;
  }

  .ui-row-trailing {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
  }

  .ui-row-under {
    flex: 1 0 100%;
    min-width: 0;
  }

  .ui-row-chevron {
    flex: 0 0 auto;
    display: inline-flex;
    margin-left: 0.75rem;
    color: var(--ui-muted);
  }
</style>
